import { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { useCapacityStore } from '../store/CapacityPlannerContext';
import { GLOBAL_DASHBOARD_ID } from '../store/CapacityPlannerStore';
import CPVersionHistory from './CPVersionHistory';
import { toast } from 'react-toastify';

// ---------------------------------------------------------------------------
// Publish dialog — two options: Share or Promote to Global Active
// ---------------------------------------------------------------------------
const PublishDialog = observer(({
  visible, onHide, dashboardId,
}: { visible: boolean; onHide: () => void; dashboardId: string }) => {
  const store = useCapacityStore();
  const [publishing, setPublishing] = useState(false);

  const record     = store.dashboardRecords.find((d) => d.id === dashboardId);
  const globalRec  = store.dashboardRecords.find((d) => d.id === GLOBAL_DASHBOARD_ID);
  if (!record || !globalRec) return null;

  const maxMajor = Math.max(
    ...globalRec.versions.map((v) => {
      const m = v.versionId.match(/^v(\d+)/);
      return m ? parseInt(m[1], 10) : 0;
    }),
  );
  const nextGlobalVersion = `v${maxMajor + 1}.0`;

  const handlePublish = async () => {
    setPublishing(true);
    await store.promoteLocalToGlobal(dashboardId);
    toast.success(
      `"${record.name}" is now the global active dashboard (${nextGlobalVersion}).`,
      { position: 'bottom-right', autoClose: 5000 },
    );
    setPublishing(false);
    onHide();
  };

  return (
    <Dialog
      header={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <i className="pi pi-star-fill" style={{ color: '#f59e0b' }} />
          <span>Promote to Global Active Version</span>
        </div>
      }
      visible={visible}
      onHide={onHide}
      style={{ width: 460 }}
      modal
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button label="Cancel" severity="secondary" text size="small" onClick={onHide} disabled={publishing} />
          <Button
            label={publishing ? 'Promoting…' : `Promote to ${nextGlobalVersion} →`}
            icon={publishing ? 'pi pi-spin pi-spinner' : 'pi pi-star-fill'}
            size="small"
            style={{ background: '#522d6e', borderColor: '#522d6e' }}
            onClick={handlePublish}
            disabled={publishing}
          />
        </div>
      }
    >
      <div style={{ padding: '4px 0 8px' }}>
        <p style={{ margin: '0 0 16px', fontSize: 13, color: '#475569' }}>
          Promote <strong>"{record.name}"</strong> as the new active version of the global{' '}
          <strong>"{globalRec.name}"</strong>?
        </p>

        <div style={{
          fontSize: 12, color: '#374151',
          background: '#f8f5fb', border: '1px solid #d4b8e8',
          borderRadius: 8, padding: '14px 16px', lineHeight: 1.6,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <span style={{
              fontSize: 13, fontWeight: 700, color: '#522d6e',
              background: '#ede3f7', padding: '2px 10px', borderRadius: 6,
            }}>
              {globalRec.activeVersionId}
            </span>
            <span style={{ color: '#94a3b8', fontSize: 13 }}>→</span>
            <span style={{
              fontSize: 13, fontWeight: 700, color: '#fff',
              background: '#522d6e', padding: '2px 10px', borderRadius: 6,
            }}>
              {nextGlobalVersion}
            </span>
          </div>
          <p style={{ margin: 0, color: '#64748b' }}>
            All users will land on <strong style={{ color: '#522d6e' }}>{nextGlobalVersion}</strong> when they open
            Capacity Planner. The current active version ({globalRec.activeVersionId}) is preserved in version history.
          </p>
        </div>
      </div>
    </Dialog>
  );
});

// ---------------------------------------------------------------------------
// CPVersionBar — main component
// ---------------------------------------------------------------------------

const CPVersionBar = observer(() => {
  const store = useCapacityStore();
  const [showHistory, setShowHistory]   = useState(false);
  const [showPublish, setShowPublish]   = useState(false);

  const { activeDashboardId, activeDashboardVersionId } = store;
  if (!activeDashboardId) return null;

  const record = store.dashboardRecords.find((d) => d.id === activeDashboardId);
  if (!record) return null;

  const isGlobalDash    = activeDashboardId === GLOBAL_DASHBOARD_ID;
  const activeVersionId = record.activeVersionId;
  const isOnActive      = activeDashboardVersionId === activeVersionId;
  const currentVer      = record.versions.find((v) => v.versionId === activeDashboardVersionId);
  const activeVer       = record.versions.find((v) => v.versionId === activeVersionId);

  const sep = <span style={{ color: '#e2e8f0', fontSize: 18, margin: '0 4px' }}>|</span>;

  return (
    <>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6,
        padding: '0 1.25rem', height: 44,
        background: '#fff',
        borderBottom: '1px solid #e2e8f0',
        flexShrink: 0,
        flexWrap: 'nowrap',
        overflow: 'hidden',
      }}>

        {/* ← Workspaces */}
        <button
          onClick={() => store.backToLanding()}
          style={{
            display: 'flex', alignItems: 'center', gap: 4,
            background: 'none', border: 'none', cursor: 'pointer',
            color: '#64748b', fontSize: 12, fontWeight: 500, padding: '2px 4px',
            flexShrink: 0, whiteSpace: 'nowrap',
          }}
        >
          <i className="pi pi-chevron-left" style={{ fontSize: 11 }} /> Workspaces
        </button>

        {sep}

        {/* Dashboard name — truncated with tooltip on hover */}
        <span
          title={record.name}
          style={{
            fontWeight: 700, fontSize: 14, color: '#1e293b',
            maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            flexShrink: 1,
          }}
        >
          {record.name}
        </span>

        {/* PUBLIC badge for global */}
        {isGlobalDash && (
          <span style={{
            fontSize: 9, fontWeight: 800, color: '#7c3aed',
            background: 'rgba(124,58,237,0.08)', border: '1px solid rgba(124,58,237,0.25)',
            padding: '2px 7px', borderRadius: 8, letterSpacing: '0.06em',
            flexShrink: 0,
          }}>
            PUBLIC
          </span>
        )}

        {/* ── Global / Published dashboard: version controls ── */}
        {(isGlobalDash || record.isPublished) && (
          <>
            {sep}

            {/* Current version button — opens history */}
            <button
              onClick={() => setShowHistory(true)}
              title={currentVer?.description}
              style={{
                display: 'flex', alignItems: 'center', gap: 5,
                padding: '4px 10px', borderRadius: 8,
                background: isOnActive ? '#fef3c7' : '#f1f5f9',
                border: `1px solid ${isOnActive ? '#fcd34d' : '#e2e8f0'}`,
                color: isOnActive ? '#92400e' : '#475569',
                fontSize: 12, fontWeight: 700, cursor: 'pointer',
                flexShrink: 0, whiteSpace: 'nowrap',
                transition: 'all 0.15s',
              }}
            >
              {isOnActive && <span style={{ color: '#f59e0b' }}>★&nbsp;</span>}
              <span style={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {currentVer?.label ?? activeDashboardVersionId}
              </span>
              <i className="pi pi-chevron-down" style={{ fontSize: 10 }} />
            </button>

            {/* Version count / history button */}
            <button
              onClick={() => setShowHistory(true)}
              style={{
                display: 'flex', alignItems: 'center', gap: 5,
                padding: '4px 10px', borderRadius: 8,
                background: '#f8f5fb', border: '1px solid #ede3f7',
                color: '#7c3aed', fontSize: 11, fontWeight: 600,
                cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap',
                transition: 'all 0.15s',
              }}
            >
              <i className="pi pi-history" style={{ fontSize: 11 }} />
              {record.versions.length} version{record.versions.length !== 1 ? 's' : ''}
            </button>

            {/* "Older version" warning + back-to-active */}
            {!isOnActive && (
              <>
                <span style={{
                  fontSize: 10, fontWeight: 700, color: '#dc2626',
                  background: '#fee2e2', border: '1px solid #fca5a5',
                  padding: '2px 8px', borderRadius: 6,
                  flexShrink: 0, whiteSpace: 'nowrap',
                }}>
                  OLDER VERSION
                </span>
                <button
                  onClick={() => store.switchVersion(activeDashboardId, activeVersionId)}
                  title={`Go to ${activeVersionId}: ${activeVer?.description ?? ''}`}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 5,
                    padding: '4px 11px', borderRadius: 8,
                    background: '#fef3c7', border: '1px solid #fcd34d',
                    color: '#92400e', fontSize: 11, fontWeight: 700,
                    cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap',
                  }}
                >
                  <i className="pi pi-star-fill" style={{ fontSize: 10 }} /> Back to Active ({activeVersionId})
                </button>
              </>
            )}
          </>
        )}

        {/* ── Private draft: draft badge + publish button ── */}
        {!record.isPublished && (
          <>
            {sep}
            <span style={{
              fontSize: 10, fontWeight: 700, color: '#b45309',
              background: '#fef3c7', padding: '3px 9px',
              border: '1px solid #fcd34d', borderRadius: 10, flexShrink: 0,
            }}>
              DRAFT · Private
            </span>
            <button
              onClick={() => setShowPublish(true)}
              style={{
                display: 'flex', alignItems: 'center', gap: 5,
                padding: '5px 13px', borderRadius: 8,
                background: '#522d6e', color: '#fff',
                border: 'none', fontSize: 12, fontWeight: 600,
                cursor: 'pointer', flexShrink: 0,
              }}
            >
              <i className="pi pi-send" style={{ fontSize: 11 }} /> Publish
            </button>
          </>
        )}
      </div>

      {/* ── Dialogs ── */}
      <CPVersionHistory visible={showHistory} onHide={() => setShowHistory(false)} />

      {showPublish && (
        <PublishDialog
          visible={showPublish}
          onHide={() => setShowPublish(false)}
          dashboardId={activeDashboardId}
        />
      )}
    </>
  );
});

export default CPVersionBar;
